// src/features/streams/components/StreamToolbar.tsx

import { Group, TextInput, Switch, Text, Button, ActionIcon, Tooltip, CloseButton } from '@mantine/core';
import {
  IconSearch,
  IconTags,
  IconTrash,
  IconPlayerPlay,
  IconPlayerPause,
  IconPlaylistAdd,
  IconPlus,
  IconX,
} from '@tabler/icons-react';
import { useSelection } from '@/shared/contexts/SelectionContext';
import type { SearchMode } from '@/lib/types/Media';
import styles from './StreamToolbar.module.css';

interface StreamToolbarProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  searchMode: SearchMode;
  onSearchModeChange: (mode: SearchMode) => void;
  totalCount: number;
  resultCount: number;
  hiddenSelectedCount?: number;
  onBulkActivate: () => void;
  onBulkDeactivate: () => void;
  onBatchTags: () => void;
  onAddToPlaylist: () => void;
  onBulkDelete: () => void;
  onCreateStream: () => void;
  onManageTags: () => void;
  activateLoading?: boolean;
  deactivateLoading?: boolean;
  deleteLoading?: boolean;
  createDisabled?: boolean;
  createDisabledReason?: string;
}

export function StreamToolbar({
  searchText,
  onSearchChange,
  searchMode,
  onSearchModeChange,
  totalCount,
  resultCount,
  hiddenSelectedCount = 0,
  onBulkActivate,
  onBulkDeactivate,
  onBatchTags,
  onAddToPlaylist,
  onBulkDelete,
  onCreateStream,
  onManageTags,
  activateLoading,
  deactivateLoading,
  deleteLoading,
  createDisabled,
  createDisabledReason,
}: StreamToolbarProps) {
  const { selectedCount, clearSelection } = useSelection();

  const hasSelection = selectedCount > 0;
  const isFiltered = searchText.trim().length > 0;

  const createButton = (
    <Button
      leftSection={<IconPlus size={16} />}
      onClick={onCreateStream}
      disabled={createDisabled}
    >
      Create Stream
    </Button>
  );

  return (
    <Group justify="space-between" className={styles.toolbar}>
      <Group>
        <TextInput
          placeholder="Search title, tags, description, ref id..."
          leftSection={<IconSearch size={16} />}
          rightSection={searchText ? (
            <CloseButton size="sm" onClick={() => onSearchChange('')} aria-label="Clear search" />
          ) : undefined}
          value={searchText}
          onChange={e => onSearchChange(e.currentTarget.value)}
          className={styles.searchInput}
        />

        <Switch
          label="Exact match"
          checked={searchMode === 'exact'}
          onChange={(e) => onSearchModeChange(e.currentTarget.checked ? 'exact' : 'fuzzy')}
          size="sm"
        />

        <Text size="sm" c="dimmed">
          {isFiltered
            ? `${resultCount} of ${totalCount} streams`
            : `${totalCount} streams`
          }
        </Text>
      </Group>

      {hasSelection ? (
        <Group gap="xs">
          <Text size="sm" fw={500}>
            {selectedCount} selected
          </Text>
          {hiddenSelectedCount > 0 && (
            <Text size="xs" c="dimmed">
              ({hiddenSelectedCount} hidden by current search)
            </Text>
          )}

          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlayerPlay size={14} />}
            onClick={onBulkActivate}
            loading={activateLoading}
          >
            Activate
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlayerPause size={14} />}
            onClick={onBulkDeactivate}
            loading={deactivateLoading}
          >
            Deactivate
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconTags size={14} />}
            onClick={onBatchTags}
          >
            Tags
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlaylistAdd size={14} />}
            onClick={onAddToPlaylist}
          >
            Add to Playlist
          </Button>
          <Button
            size="xs"
            variant="light"
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={onBulkDelete}
            loading={deleteLoading}
          >
            Delete
          </Button>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={clearSelection}
            aria-label="Clear selection"
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
      ) : (
        <Group gap="sm">
          <Button
            variant="default"
            leftSection={<IconTags size={16} />}
            onClick={onManageTags}
          >
            Manage Tags
          </Button>
          {createDisabled && createDisabledReason ? (
            <Tooltip label={createDisabledReason}>
              <span>{createButton}</span>
            </Tooltip>
          ) : createButton}
        </Group>
      )}
    </Group>
  );
}
